import { Accept, useDropzone, FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Typography, useTheme } from '@mui/material';
import { IconFileDescription } from '@tabler/icons-react';
import { Button } from '@atoms';
import { DEFAULT_ACCEPTED_FILES_CSV } from '@globalConstants';
import styles from './styles';

interface DropzoneProps {
  files: FileWithPath[];
  setFiles: (files: FileWithPath[]) => void;
  open?: boolean;
  totalFiles?: number;
  dragText?: string;
  text?: string;
  buttonText?: string;
  error?: object;
  acceptedFilesTypes?: object;
  handleFilesSelection: (files: FileWithPath[]) => void;
  onClear?: () => void;
  disabled: boolean;
}
/**
 * render a drag & drop component
 */
const Dropzone = (props: DropzoneProps) => {
  const { t } = useTranslation();
  const { palette } = useTheme();

  const {
    files,
    setFiles,
    totalFiles = 1,
    acceptedFilesTypes = DEFAULT_ACCEPTED_FILES_CSV,
    dragText = 'COMMON:FILE_UPLOADS:DRAG_AND_DROP',
    text = 'COMMON:FILE_UPLOADS:DROPZONE_TEXT',
    buttonText = 'COMMON:FILE_UPLOADS:BUTTON',
    handleFilesSelection,
    disabled = false,
    onClear,
  } = props;

  const {
    fileRejections,
    isDragActive,
    getRootProps,
    getInputProps,
    open: openSelectFile,
  } = useDropzone({
    maxFiles: totalFiles,
    accept: acceptedFilesTypes as Accept,
    onDrop: handleFilesSelection,
    disabled,
    noClick: true,
  });

  /**
   * @returns Selected file info
   */
  const getSelectionInfo = () => (
    <Box sx={styles.selectionInfo}>
      <IconFileDescription color={palette.primary.light} size='1.5rem' />
      <Typography variant='body2' color='gray.80'>
        {files[0].path}
      </Typography>
    </Box>
  );

  const hasSelectedFile = files.length > 0;
  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => {
    const fileParsed: File & { path: string } = { path: '', ...file };
    return (
      <div key={index}>
        {errors.map((e) => (
          <Alert sx={{ mt: 2 }} key={e.code} severity='error'>
            {fileParsed.path}
            <strong>{` ${e.message}`}</strong>
          </Alert>
        ))}
      </div>
    );
  });
  return (
    <>
      <Box {...getRootProps({ className: 'dropzone' })} sx={styles.dropContainer}>
        <input {...getInputProps()} name='file' multiple={false} />
        {isDragActive ? (
          <Typography>{t(dragText)}</Typography>
        ) : hasSelectedFile ? (
          <Typography>{getSelectionInfo()}</Typography>
        ) : (
          <Typography textAlign='center' variant='body1' color='gray.90'>
            {t(text)}
            <br />o
          </Typography>
        )}
        <Box sx={styles.selectionButtonBox}>
          {hasSelectedFile ? (
            <Button
              onClick={() => {
                onClear?.();
                setFiles([]);
              }}
              text='COMMON:CLEAR'
              variant='outlined'
              color='primary'
            />
          ) : (
            <Button
              onClick={openSelectFile}
              text={t(buttonText)}
              disabled={disabled}
              variant='outlined'
              color='primary'
            />
          )}
        </Box>
        <aside>{fileRejectionItems}</aside>
      </Box>
    </>
  );
};

export default Dropzone;
